import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { captureException, captureMessage } from '@sentry/react';
import {
  addParamsToUrl,
  extractParams,
  removeParamsFromUrl,
} from '@zapier/url-utils';

import { RouteTo, SignupType } from '../../../types';
import { defaultRouteTo } from '../../../utils';
import { signupTypeToAuthType, storeAuthType } from '../../../hooks';
import {
  flushWithin,
  TRACKING_FLUSH_TIMEOUT_MS,
  urls,
} from '@zapier/identity-utils';
import { SocialAuthButton } from '../SocialAuthButton';
import { SSOIcon } from '../SSOIcon';

export type OnTrackSignupConversion = (
  type: SignupType,
  userId: string
) => void | Promise<void>;

export type OnTrackSignupIntent = (type: SignupType) => void;

export type SSOButtonProps = {
  children: ReactNode;
  /** The SSO start url */
  href: string;
  /** The URL to redirect to after auth is complete */
  next?: string;
  /** Callback to track signup conversions */
  onTrackSignupConversion?: OnTrackSignupConversion;
  /** Callback to track signup intent */
  onTrackSignupIntent?: OnTrackSignupIntent;
  /** Params to add to href */
  params?: Record<string, string | number | boolean>;
  /** Go directly to href on click without tracking */
  passThrough?: boolean;
  /** Routing callback to integrate with router. Will use window.href.location directly when not specified */
  routeTo?: RouteTo;
  /** Opens the SSO auth in a child window if true */
  shouldOpenInChildWindow?: boolean;
  /** Type of signup that SSO Button is triggering */
  type: SignupType;
  icon?: ReactNode;
};

/**
 * Wrapper to start managed SSO flows where we have to listen for
 * messages from the SSO provider to know when it has completed
 */
export function SSOButton({
  children,
  href,
  next,
  onTrackSignupConversion,
  onTrackSignupIntent,
  params = {},
  passThrough = false,
  routeTo = defaultRouteTo,
  shouldOpenInChildWindow = true,
  type,
  icon = <SSOIcon />,
}: SSOButtonProps) {
  // Build SSO start url
  const ssoStartUrl = addParamsToUrl(href, {
    in_child_window: shouldOpenInChildWindow,
    next,
    ...params,
  });

  const onAuthComplete = useCallback(
    async (event: MessageEvent) => {
      // The listener is on `window`, so it receives every posted message on the page,
      // including ones from extensions and embedded third parties. Only the SSO popup's
      // own origin is ours to parse. Comparing `event.source` to the popup handle would
      // be stronger, but the popup is opened with `noopener` so `window.open` returns null.
      if (
        event.origin !== new URL(ssoStartUrl, window.location.origin).origin
      ) {
        return;
      }

      // Our popup always posts a JSON string, so anything else is somebody else's message
      if (typeof event.data !== 'string') {
        return;
      }

      let data = {};

      // Try to parse event data
      try {
        data = JSON.parse(event.data);

        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
      } catch (e) {
        // Same-origin message we can't parse, so our own popup broke its contract
        captureException(e, {
          extra: {
            eventDataLength: event.data.length,
          },
        });
        return;
      }

      // Try to extract nextUrl from event data
      const { nextUrl } = data as { nextUrl: string };

      if (!nextUrl) {
        // We couldn't extract the nextUrl so log and bail
        captureMessage(
          'SSOButton: Error deciphering next URL from posted window message. nextUrl is empty'
        );
        return;
      }

      // Get params from nextUrl so we know if this is a signup
      // or a login and get the id of the user that authed
      const params = extractParams(nextUrl) as {
        customuser_id: string;
        new_signup: string;
      };

      // See if this is a signup
      if (params.new_signup && onTrackSignupConversion) {
        // Flush the conversion event before we switch pages so the navigation
        // doesn't cut it off, but time-box the wait so a slow or hung tracking
        // request never strands the user. Call the callback inside .then() so a
        // synchronous throw becomes a rejection (not an unhandled error that
        // would skip the redirect); keep logging failures ourselves, since
        // flushWithin swallows rejections.
        await flushWithin(
          Promise.resolve()
            .then(() => onTrackSignupConversion(type, params.customuser_id))
            .catch(captureException),
          TRACKING_FLUSH_TIMEOUT_MS
        );
      }

      // Sanitize the nextUrl so we don't keep their user id in there
      const redirectUrl = addParamsToUrl(urls.REDIRECT_URL, {
        next: removeParamsFromUrl(nextUrl, ['customuser_id']),
      });

      // Redirect the user to the sanitized url
      routeTo(redirectUrl);
    },
    [onTrackSignupConversion, routeTo, ssoStartUrl, type]
  );

  const onAuthCompleteRef = useRef(onAuthComplete);
  useEffect(() => {
    onAuthCompleteRef.current = onAuthComplete;
  }, [onAuthComplete]);

  // `onMessage` is registered on click and removed on unmount, so its identity
  // has to stay stable for the removal to match. It reads the current handler
  // through a ref to keep an empty dependency list.
  const onMessage = useCallback((event: MessageEvent) => {
    onAuthCompleteRef.current(event);
  }, []);

  useEffect(() => {
    // Ensure that we stop listening for events on dismount
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const authType = signupTypeToAuthType(type);

      if (authType) {
        storeAuthType(authType);
      }

      if (passThrough && next) {
        // Go directly to href without tracking
        routeTo(next);
        return;
      }

      // Track that the user wanted to sign up
      if (onTrackSignupIntent) {
        onTrackSignupIntent(type);
      }

      if (shouldOpenInChildWindow) {
        // Starting the auth flow in a child window so add an event listener
        // so we know when the auth has completed
        window.addEventListener('message', onMessage);

        // Open SSO in a _blank named window with 'noopener' to prevent reverse
        // tabnabbing attacks via window.opener access. Not having a named popup prevents
        // malicious iframes from hijacking the popup.
        window.open(
          ssoStartUrl,
          '_blank',
          'noopener,height=600,width=980,resizable=yes,scrollbars=yes'
        );
      } else {
        // Start the flow in current window
        routeTo(ssoStartUrl);
      }
    },
    [
      next,
      onMessage,
      onTrackSignupIntent,
      passThrough,
      routeTo,
      shouldOpenInChildWindow,
      ssoStartUrl,
      type,
    ]
  );

  return (
    <SocialAuthButton href={ssoStartUrl} icon={icon} onClick={onClick}>
      {children}
    </SocialAuthButton>
  );
}
